import useQueryBuildings from "ApiLayer/Building/queryBuildings";
import { Item } from "components/atoms/Dropdown";
import ChartContainer from "components/molecules/ChartContainer";
import { BarChart } from "components/molecules/Charts";
import { DATA_KEY_COLORS } from "constants/chartsColors.constant";
import { ReactNode, useState } from "react";
import {
    CarbonPolicyCostChartGroup,
    carbonPolicyCostChartGroupOptions,
    carbonPolicyCostChartTooltip,
} from "utils/constants";
import { getDisplayInfo } from "utils/formatting";
import { dataKeys, getCarbonPolicyAggregator, titleKey } from "./CumulativeCarbonPolicyCostAggregator";
import { getCarbonPolicyData } from "./CumulativeCarbonPolicyCostChart.helpers";

function CumulativeCarbonPolicyCostChart({
    filtersetUid,
    reportedVsPublished,
}: {
    filtersetUid: string;
    reportedVsPublished: string;
}) {
    const [carbonPolicyCostChartGroup, setCarbonPolicyCostChartGroup] =
        useState<CarbonPolicyCostChartGroup>("carbonPolicy");
    const { data: buildingsResponse, isLoading } = useQueryBuildings(filtersetUid, reportedVsPublished);

    const ungroupedCarbonPolicyData = buildingsResponse?.buildings
        ? getCarbonPolicyData(buildingsResponse?.buildings)
        : [];
    const carbonPolicyAggregator = getCarbonPolicyAggregator(carbonPolicyCostChartGroup);
    const carbonPolicyBarChartData = carbonPolicyAggregator(ungroupedCarbonPolicyData);

    const chartKeys = dataKeys.map((key) => getDisplayInfo(key).humanReadable);
    const indexKey = getDisplayInfo(titleKey).humanReadable;
    const maxValue = Math.max(
        ...carbonPolicyBarChartData.barChartData.flatMap((row) => dataKeys.map((key) => Number(row[key]) || 0))
    );

    const { unit, formatFunction } = getDisplayInfo(dataKeys[0], maxValue);
    const label = unit;
    const barData = carbonPolicyBarChartData.barChartData.map((groupedDatum) =>
        Object.fromEntries(
            Object.entries(groupedDatum).map(([key, value]) => [getDisplayInfo(key).humanReadable, value])
        )
    );

    const keysToColours = chartKeys.map((chartKey, i) => ({
        label: chartKey,
        color: DATA_KEY_COLORS[chartKey],
    }));

    return (
        <CumulativeCarbonPolicyCostChartContainer
            setCarbonPolicyCostChartGroup={setCarbonPolicyCostChartGroup}
            isLoading={isLoading}
            cumulativeCarbonFines={carbonPolicyBarChartData.cumulativeCarbonFines}
            cumulativeCarbonFineSavings={carbonPolicyBarChartData.cumulativeCarbonFineSavings}
            barData={barData}
        >
            <BarChart
                data={barData}
                indexBy={indexKey}
                chartKeys={chartKeys}
                chartColorsMappedToKeys={keysToColours}
                yAxisInfo={{
                    formatFunction: (v) => formatFunction(v, false),
                    label,
                }}
                includeLegend={true}
            />
        </CumulativeCarbonPolicyCostChartContainer>
    );
}

interface CumulativeCarbonPolicyCostChartContainerProps {
    children: ReactNode;
    setCarbonPolicyCostChartGroup: Function;
    isLoading: boolean;
    cumulativeCarbonFines?: number;
    cumulativeCarbonFineSavings?: number;
    barData: any;
}

function CumulativeCarbonPolicyCostChartContainer({
    children,
    setCarbonPolicyCostChartGroup,
    isLoading,
    cumulativeCarbonFines,
    cumulativeCarbonFineSavings,
    barData,
}: CumulativeCarbonPolicyCostChartContainerProps) {
    const items: Item[] = carbonPolicyCostChartGroupOptions.map(({ key, label }: CarbonPolicyCostChartGroupOption) => ({
        id: key,
        displayValue: label,
    }));

    const defaultItem = items.find((item) => item.id === "carbonPolicy");
    const { unit: finesUnit, formatFunction: finesFormatFunction } = getDisplayInfo(
        "cumulativeCarbonFines",
        cumulativeCarbonFines
    );
    const { unit: savingsUnit, formatFunction: savingsFormatFunction } = getDisplayInfo(
        "cumulativeCarbonFineSavings",
        cumulativeCarbonFineSavings
    );

    return (
        <ChartContainer
            title="Cumulative carbon policy cost savings by "
            onSelectItem={(x) => setCarbonPolicyCostChartGroup(x)}
            items={items}
            loading={isLoading}
            defaultDropdownItem={defaultItem}
            tooltip={carbonPolicyCostChartTooltip}
            exportableData={barData}
        >
            {cumulativeCarbonFines && cumulativeCarbonFineSavings && (
                <>
                    <h2>
                        {savingsFormatFunction(cumulativeCarbonFineSavings)} {savingsUnit}
                    </h2>
                    <p>
                        from {finesFormatFunction(cumulativeCarbonFines)} {finesUnit} in cumulative carbon policy costs
                    </p>
                </>
            )}
            {children}
        </ChartContainer>
    );
}

export default CumulativeCarbonPolicyCostChart;
